// src/components/artwork/tabs/ArtworkProvenanceAcquisitionTab.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import AcquisitionSection from "../forms/AcquisitionSection";
import ArtworkProvenanceTab from "./ArtworkProvenanceTab";

const setByPath = (source, path, value) => {
  const keys = String(path || "").split(".").filter(Boolean);
  if (!keys.length) return source;

  const clone = { ...(source || {}) };
  let cursor = clone;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }

    const next = cursor[key];
    cursor[key] = next && typeof next === "object" && !Array.isArray(next) ? { ...next } : {};
    cursor = cursor[key];
  });

  return clone;
};

export default function ArtworkProvenanceAcquisitionTab({
  artwork,
  formData,
  setFormData,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const P = "artworkEdit.provenanceTab";
  const data = formData || artwork || {};

  const handleInputChange = (eventOrPatch) => {
    if (readOnly || !setFormData) return;

    const target = eventOrPatch?.target;
    const name = target?.name;
    if (!name) return;

    const value = target?.type === "checkbox" ? !!target.checked : target?.value;

    setFormData((previous) => {
      const base = typeof previous === "function" ? previous(data) : previous || data || {};
      return setByPath(base, name, value);
    });
  };

  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-5">
          <p className="text-xs uppercase tracking-[0.16em] text-gray-500" style={{ fontFamily: "Georgia, serif" }}>
            {t(`${P}.entryEyebrow`)}
          </p>
          <h2 className="mt-1 text-xl font-serif text-gray-950">
            {t(`${P}.entryTitle`)}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t(`${P}.entryHelp`)}
          </p>
        </div>

        <AcquisitionSection
          formData={data}
          handleInputChange={handleInputChange}
          readOnly={readOnly}
          embedded
        />
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-5">
          <p className="text-xs uppercase tracking-[0.16em] text-gray-500" style={{ fontFamily: "Georgia, serif" }}>
            {t(`${P}.journeyEyebrow`)}
          </p>
          <h2 className="mt-1 text-xl font-serif text-gray-950">
            {t(`${P}.journeyTitle`)}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t(`${P}.journeyHelp`)}
          </p>
        </div>

        <ArtworkProvenanceTab
          artwork={artwork}
          formData={data}
          setFormData={setFormData}
          readOnly={readOnly}
        />
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.16em] text-gray-500" style={{ fontFamily: "Georgia, serif" }}>
            {t(`${P}.verificationEyebrow`, { defaultValue: "Point de vigilance" })}
          </p>
          <h2 className="mt-1 text-xl font-serif text-gray-950">
            {t(`${P}.verificationTitle`, { defaultValue: "Propriété à vérifier" })}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t(`${P}.verificationHelp`, {
              defaultValue:
                "À cocher si la situation de propriété déclarée n'est pas certaine (indivision possible, régime matrimonial, donation non formalisée...). Cette information ne détermine pas la propriété juridique — elle signale un point à faire vérifier par un notaire.",
            })}
          </p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="ownership_verification"
            checked={data.ownership_verification === "to_verify"}
            disabled={readOnly}
            onChange={(e) =>
              handleInputChange({
                target: {
                  name: "ownership_verification",
                  type: "text",
                  value: e.target.checked ? "to_verify" : "confirmed",
                },
              })
            }
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-800">
            {t(`${P}.verificationCheckboxLabel`, {
              defaultValue: "La propriété de cette œuvre reste à vérifier",
            })}
          </span>
        </label>

        {data.ownership_verification === "to_verify" && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t(`${P}.verificationNoteLabel`, { defaultValue: "Note (facultative)" })}
            </label>
            <textarea
              name="ownership_verification_note"
              value={data.ownership_verification_note || ""}
              disabled={readOnly}
              onChange={handleInputChange}
              rows={3}
              placeholder={t(`${P}.verificationNotePlaceholder`, {
                defaultValue: "Ex : acquise pendant le mariage, régime matrimonial à vérifier...",
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-gray-500 focus:outline-none disabled:bg-gray-50"
            />
          </div>
        )}
      </section>
    </div>
  );
}
